import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { PageHeader, Card, money, fmtDate, Th, Td, DateRangeFilter, rangeParams } from "../components/shared";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { JourneyDialog } from "./Customers";
import { ChevronLeft, ChevronRight, Inbox, CheckCircle2, CopyX, AlertTriangle } from "lucide-react";

const OUTCOME_STYLES = {
  recorded: "bg-emerald-50 text-emerald-700 border-emerald-200",
  duplicate: "bg-amber-50 text-amber-700 border-amber-200",
  unmatched: "bg-rose-50 text-rose-700 border-rose-200",
};

const SummaryCard = ({ icon: Icon, label, value, sub, color }) => (
  <Card className="p-4">
    <div className="flex items-center gap-2 mb-2">
      <div className={`h-7 w-7 rounded-md flex items-center justify-center ${color}`}>
        <Icon className="h-3.5 w-3.5" />
      </div>
      <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</span>
    </div>
    <div className="font-mono text-xl font-semibold text-slate-900">{value}</div>
    {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
  </Card>
);

export default function Reconciliation() {
  const [data, setData] = useState(null);
  const [rangeSel, setRangeSel] = useState({ range: "today" });
  const [outcome, setOutcome] = useState("");
  const [page, setPage] = useState(0);
  const [journeyClick, setJourneyClick] = useState(null);
  const limit = 25;

  const load = useCallback(() => {
    api.get(`/reconciliation?${rangeParams(rangeSel)}&outcome=${outcome}&limit=${limit}&skip=${page * limit}`).then((r) => setData(r.data));
  }, [rangeSel, outcome, page]);
  useEffect(load, [load]);

  const s = data?.summary;
  return (
    <div data-testid="reconciliation-page">
      <PageHeader
        title="Reconciliation"
        subtitle="Every postback received from networks — including unmatched ones (missed / broken click IDs)"
        action={<DateRangeFilter value={rangeSel} onChange={(v) => { setRangeSel(v); setPage(0); }} />}
      />
      {s && (
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-6 fade-up">
          <SummaryCard icon={Inbox} label="Postbacks Received" value={s.total.toLocaleString()} color="bg-indigo-50 text-indigo-600" />
          <SummaryCard icon={CheckCircle2} label="Recorded" value={s.recorded.toLocaleString()} sub={`${money(s.recorded_revenue)} revenue`} color="bg-emerald-50 text-emerald-600" />
          <SummaryCard icon={CopyX} label="Duplicates" value={s.duplicates.toLocaleString()} sub="Same click + tx re-fired" color="bg-amber-50 text-amber-600" />
          <SummaryCard icon={AlertTriangle} label="Unmatched" value={s.unmatched.toLocaleString()} sub={`${money(s.unmatched_revenue)} at risk`} color="bg-rose-50 text-rose-600" />
        </div>
      )}
      <div className="mb-4 fade-up">
        <Select value={outcome || "all"} onValueChange={(v) => { setOutcome(v === "all" ? "" : v); setPage(0); }}>
          <SelectTrigger data-testid="reconciliation-outcome-filter" className="w-48 bg-white"><SelectValue placeholder="All outcomes" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All outcomes</SelectItem>
            <SelectItem value="recorded">Recorded</SelectItem>
            <SelectItem value="duplicate">Duplicates</SelectItem>
            <SelectItem value="unmatched">Unmatched</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full">
          <thead><tr><Th>Time (EST)</Th><Th>Outcome</Th><Th>Network</Th><Th>Click ID</Th><Th>Tx ID</Th><Th>Offer</Th><Th className="text-right">Payout</Th><Th>Status</Th></tr></thead>
          <tbody data-testid="reconciliation-table-body">
            {data?.items.map((p) => (
              <tr
                key={p.id}
                className={`border-t border-slate-100 ${p.outcome !== "unmatched" ? "hover:bg-indigo-50/40 cursor-pointer" : "hover:bg-rose-50/40"}`}
                onClick={() => p.outcome !== "unmatched" && setJourneyClick(p.click_id)}
              >
                <Td className="whitespace-nowrap text-xs">{fmtDate(p.timestamp)}</Td>
                <Td><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${OUTCOME_STYLES[p.outcome]}`}>{p.outcome}</span></Td>
                <Td className="text-xs">{p.network_name || "Unknown"}</Td>
                <Td><code className={`text-[11px] ${p.outcome === "unmatched" ? "text-rose-600" : "text-indigo-600"}`}>{p.click_id || "—"}</code></Td>
                <Td><code className="text-[11px]">{p.tx_id || "—"}</code></Td>
                <Td className="text-xs">{p.offer_name || "—"}</Td>
                <Td className="text-right font-mono font-semibold">{money(p.payout)}</Td>
                <Td className="text-xs">{p.status}</Td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {data && data.items.length === 0 && <div className="p-8 text-center text-sm text-slate-400">No postbacks received in this period.</div>}
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
          <span className="text-xs text-slate-400">Page {page + 1} of {Math.max(1, Math.ceil((data?.total || 0) / limit))}</span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" className="h-7" disabled={page === 0} onClick={() => setPage((p) => p - 1)}><ChevronLeft className="h-3.5 w-3.5" /></Button>
            <Button variant="outline" size="sm" className="h-7" disabled={(page + 1) * limit >= (data?.total || 0)} onClick={() => setPage((p) => p + 1)}><ChevronRight className="h-3.5 w-3.5" /></Button>
          </div>
        </div>
      </Card>
      <JourneyDialog clickId={journeyClick} onClose={() => setJourneyClick(null)} />
    </div>
  );
}
